import { ref } from 'vue';
import { createAuthenticatedApiClient } from '../services/ApiClient';
import { useAuth } from './useAuth';
import type { StorageUsageResponse } from '@maso-dj/shared-types';
import type { Ref } from 'vue';

const authApiClient = createAuthenticatedApiClient();

// Shared state across all instances
const storageData = ref<StorageUsageResponse | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const lastFetchTime = ref<number>(0);

// Cache duration: 30 seconds
const CACHE_DURATION = 30 * 1000;

export function useStorageUsage(): {
  storageData: Ref<StorageUsageResponse | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  fetchStorageUsage: (forceRefresh?: boolean) => Promise<void>;
  refresh: () => Promise<void>;
} {
  const auth = useAuth();

  const fetchStorageUsage = async (forceRefresh = false): Promise<void> => {
    // Check if we should use cached data
    const now = Date.now();
    if (!forceRefresh && storageData.value && (now - lastFetchTime.value) < CACHE_DURATION) {
      return;
    }

    try {
      loading.value = true;
      error.value = null;

      // Set up authentication
      authApiClient.setTokenProvider(auth.getToken);

      const response = await authApiClient.get<StorageUsageResponse>(
        '/api/subscriptions/storage-usage',
        {
          credentials: 'include',
          description: 'Fetch storage usage'
        }
      );

      if (response.success) {
        storageData.value = response;
        lastFetchTime.value = now;
      } else {
        error.value = 'Failed to load storage usage';
      }
    } catch (err) {
      console.error('Error fetching storage usage:', err);
      error.value = 'Failed to load storage usage';
    } finally {
      loading.value = false;
    }
  };

  const refresh = (): Promise<void> => {
    return fetchStorageUsage(true);
  };

  return {
    storageData,
    loading,
    error,
    fetchStorageUsage,
    refresh
  };
}
